/**
 * Tab „Context” on the project page.
 *
 * Three things live here:
 *  1. The CONSOLIDATED CONTEXT DOCUMENT (projects/<slug>/context.md in the KB):
 *     the project's story as told to Firmio AI. Readable by anyone who can see
 *     the overview, editable by hand by anyone who can edit the project.
 *  2. A CHAT WITH FIRMIO AI scoped to this project: the backend gets
 *     `projectContextId`, injects the document into the system prompt and has
 *     the assistant save everything the user tells it (project_context_add);
 *     the document refreshes whenever that tool completes.
 *  3. The JOURNAL of raw additions (chat / direct note / voice), newest first,
 *     plus a "direct note" box for people who prefer typing over chatting.
 *
 * The chat thread is a normal Firmio AI conversation (persisted server-side
 * and visible in /chat); we only remember its id per project in localStorage
 * so coming back to the tab continues the same conversation.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Check,
  Copy,
  ExternalLink,
  Loader2,
  MessageSquarePlus,
  Pencil,
  Plus,
  Send,
  Square,
  User,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import type { ProjectDetail } from '../../lib/projectsApi';
import { getConversation, streamChat, toolResultToCard, type ChatMessage } from '../../lib/chatApi';
import type { ProjectContextEntry } from '../../lib/projectContextApi';
import {
  projectContextKey,
  useAddProjectContextEntry,
  useProjectContext,
  useSaveProjectContextDocument,
} from '../../hooks/useProjectContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useI18n } from '../../contexts/I18nContext';
import { useTestMode } from '../testMode/TestModeContext';
import { Markdown } from '../chat/Markdown';
import { FirmioAiAvatar } from '../brand/FirmioAiIcon';
import { formatDate } from '../../lib/format';
import { SkeletonText } from '../ui/Skeleton';

const CONVERSATION_KEY_PREFIX = 'firmio-project-context-conversation:';

function readStoredConversation(projectId: string): string | null {
  try {
    return localStorage.getItem(CONVERSATION_KEY_PREFIX + projectId);
  } catch {
    return null;
  }
}

function storeConversation(projectId: string, conversationId: string | null): void {
  try {
    if (conversationId) localStorage.setItem(CONVERSATION_KEY_PREFIX + projectId, conversationId);
    else localStorage.removeItem(CONVERSATION_KEY_PREFIX + projectId);
  } catch {
    // localStorage unavailable (private mode) — the thread just won't resume.
  }
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${formatDate(iso)} ${d.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })}`;
}

export function ProjectContextTab({ project }: { project: ProjectDetail }) {
  const { t } = useI18n();
  const { can } = usePermissions();
  const canEdit = can('projects', 'canEdit');
  const { data, isLoading, isError } = useProjectContext(project.id);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{t('projects.context.title')}</h2>
        <p className="mt-1 max-w-3xl text-sm text-gray-600">{t('projects.context.intro')}</p>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
        <div className="space-y-6 xl:col-span-3">
          <DocumentPanel
            projectId={project.id}
            canEdit={canEdit}
            isLoading={isLoading}
            isError={isError}
            markdown={data?.document.markdown ?? ''}
            updatedAt={data?.document.updatedAt ?? null}
            kbPath={data?.document.kbPath ?? ''}
          />
          <JournalPanel entries={data?.entries ?? []} isLoading={isLoading} />
        </div>
        <div className="space-y-6 xl:col-span-2">
          {canEdit && <ContextChat project={project} />}
          {canEdit && <DirectNote projectId={project.id} />}
        </div>
      </div>
    </div>
  );
}

// --- Document -----------------------------------------------------------------

function DocumentPanel({
  projectId,
  canEdit,
  isLoading,
  isError,
  markdown,
  updatedAt,
  kbPath,
}: {
  projectId: string;
  canEdit: boolean;
  isLoading: boolean;
  isError: boolean;
  markdown: string;
  updatedAt: string | null;
  kbPath: string;
}) {
  const { t } = useI18n();
  const save = useSaveProjectContextDocument(projectId);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [copied, setCopied] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const startEdit = () => {
    setDraft(markdown);
    setEditing(true);
    setNotice(null);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(markdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked — nothing to do; the text is on screen anyway.
    }
  };

  const submit = async () => {
    try {
      await save.mutateAsync(draft);
      setEditing(false);
      setNotice(t('projects.context.saved'));
    } catch (err) {
      setNotice(err instanceof Error ? err.message : t('projects.context.error.save'));
    }
  };

  return (
    <section className="rounded-lg border border-border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div className="min-w-0 text-xs text-gray-500">
          {updatedAt ? (
            <span>{t('projects.context.updatedAt', { date: formatDateTime(updatedAt) })}</span>
          ) : null}
          {kbPath ? (
            <span className="ml-2 hidden font-mono sm:inline">
              {t('projects.context.kbPath', { path: kbPath })}
            </span>
          ) : null}
        </div>
        <div className="flex gap-2">
          {!editing && markdown && (
            <button
              type="button"
              onClick={copy}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? t('projects.context.copied') : t('projects.context.copy')}
            </button>
          )}
          {canEdit && !editing && !isLoading && (
            <button
              type="button"
              onClick={startEdit}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              <Pencil size={14} />
              {t('projects.context.edit')}
            </button>
          )}
        </div>
      </div>

      <div className="px-4 py-4">
        {isLoading ? (
          <SkeletonText lines={6} />
        ) : isError ? (
          <p className="text-sm text-red-600">{t('projects.context.error.load')}</p>
        ) : editing ? (
          <div className="space-y-3">
            <p className="text-xs text-gray-500">{t('projects.context.editHint')}</p>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={22}
              className="w-full rounded-md border border-border bg-background p-3 font-mono text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={submit}
                disabled={save.isPending}
                className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-60"
              >
                {save.isPending && <Loader2 size={14} className="animate-spin" />}
                {t('projects.context.save')}
              </button>
              <button
                type="button"
                onClick={() => setEditing(false)}
                disabled={save.isPending}
                className="rounded-md border border-border px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {t('projects.context.cancel')}
              </button>
            </div>
          </div>
        ) : markdown ? (
          <Markdown content={markdown} />
        ) : (
          <p className="text-sm text-gray-500">{t('projects.context.empty')}</p>
        )}
        {notice && !editing && <p className="mt-3 text-xs text-gray-600">{notice}</p>}
      </div>
    </section>
  );
}

// --- Chat with Firmio AI -------------------------------------------------------

function ContextChat({ project }: { project: ProjectDetail }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { enabled: demoMode } = useTestMode();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [activeTool, setActiveTool] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(() =>
    readStoredConversation(project.id),
  );
  const cancelRef = useRef<(() => void) | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const welcome = useMemo(
    () => t('projects.context.chat.welcome', { name: project.name }),
    [t, project.name],
  );

  // Resume the stored thread (if any) so the tab continues where it left off.
  useEffect(() => {
    let cancelled = false;
    const stored = readStoredConversation(project.id);
    if (!stored) {
      setMessages([]);
      return;
    }
    getConversation(stored)
      .then((conv) => {
        if (cancelled) return;
        setMessages(conv.turns.filter((m) => m.content.trim() !== ''));
        setConversationId(conv.id);
      })
      .catch(() => {
        if (cancelled) return;
        // Thread gone (deleted / other user) — start fresh.
        storeConversation(project.id, null);
        setConversationId(null);
        setMessages([]);
      });
    return () => {
      cancelled = true;
    };
  }, [project.id]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, activeTool]);

  useEffect(() => () => cancelRef.current?.(), []);

  const stop = useCallback(() => {
    cancelRef.current?.();
    cancelRef.current = null;
    setStreaming(false);
    setActiveTool(null);
  }, []);

  const send = useCallback(() => {
    const text = input.trim();
    if (!text || streaming) return;
    setError(null);
    const userMsg: ChatMessage = { role: 'user', content: text };
    const history = [...messages, userMsg];
    setMessages([...history, { role: 'assistant', content: '' }]);
    setInput('');
    setStreaming(true);
    let contextTouched = false;

    cancelRef.current = streamChat(
      history.filter((m) => m.content.trim() !== ''),
      {
        onDelta: (chunk) => {
          setMessages((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last && last.role === 'assistant') {
              next[next.length - 1] = { ...last, content: last.content + chunk };
            }
            return next;
          });
        },
        onToolUse: (name) => setActiveTool(name),
        onToolResult: (name, result) => {
          setActiveTool(null);
          if (name === 'project_context_add') {
            contextTouched = true;
            void qc.invalidateQueries({ queryKey: projectContextKey(project.id) });
          }
          const card = toolResultToCard(name, result);
          if (!card) return;
          setMessages((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last && last.role === 'assistant') {
              next[next.length - 1] = {
                ...last,
                toolResults: [...(last.toolResults ?? []), card],
              };
            }
            return next;
          });
        },
        onError: (message) => setError(message),
        onDone: (_stopReason, newConversationId) => {
          setStreaming(false);
          setActiveTool(null);
          cancelRef.current = null;
          if (newConversationId && newConversationId !== conversationId) {
            setConversationId(newConversationId);
            storeConversation(project.id, newConversationId);
          }
          if (contextTouched) {
            void qc.invalidateQueries({ queryKey: projectContextKey(project.id) });
          }
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (
              last &&
              last.role === 'assistant' &&
              last.content.trim() === '' &&
              !(last.toolResults && last.toolResults.length > 0)
            ) {
              return prev.slice(0, -1);
            }
            return prev;
          });
        },
      },
      undefined,
      conversationId ?? undefined,
      undefined,
      { projectContextId: project.id },
    );
  }, [input, streaming, messages, conversationId, project.id, qc]);

  const newConversation = () => {
    stop();
    storeConversation(project.id, null);
    setConversationId(null);
    setMessages([]);
    setError(null);
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const toolLabel =
    activeTool === 'project_context_add'
      ? t('projects.context.chat.saving')
      : activeTool
        ? t('projects.context.chat.thinking')
        : null;

  return (
    <section className="flex flex-col rounded-lg border border-border bg-card">
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <FirmioAiAvatar size={24} />
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900">
              {t('projects.context.chat.title')}
            </h3>
            <p className="truncate text-xs text-gray-500">{t('projects.context.chat.hint')}</p>
          </div>
        </div>
        <div className="flex shrink-0 gap-1">
          {conversationId && (
            <Link
              to={`/chat/${conversationId}`}
              title={t('projects.context.chat.openFull')}
              className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            >
              <ExternalLink size={16} />
            </Link>
          )}
          <button
            type="button"
            onClick={newConversation}
            title={t('projects.context.chat.newConversation')}
            className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          >
            <MessageSquarePlus size={16} />
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="max-h-[32rem] min-h-[16rem] space-y-3 overflow-y-auto px-4 py-4"
      >
        {messages.length === 0 && (
          <div className="flex gap-3">
            <FirmioAiAvatar size={28} />
            <div className="min-w-0 flex-1 rounded-lg border border-border bg-background px-3 py-2">
              <Markdown content={welcome} />
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <Bubble key={i} message={m} />
        ))}
        {toolLabel && (
          <div className="flex items-center gap-2 pl-10 text-xs text-gray-500">
            <Loader2 size={12} className="animate-spin" />
            {toolLabel}
          </div>
        )}
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>

      <div className="border-t border-border p-3">
        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          rows={3}
          disabled={demoMode}
          placeholder={t('projects.context.chat.placeholder')}
          className="w-full resize-none rounded-md border border-border bg-background p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-60"
        />
        <div className="mt-2 flex justify-end gap-2">
          {streaming ? (
            <button
              type="button"
              onClick={stop}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Square size={14} />
              {t('projects.context.chat.stop')}
            </button>
          ) : (
            <button
              type="button"
              onClick={send}
              disabled={!input.trim() || demoMode}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-60"
            >
              <Send size={14} />
              {t('projects.context.chat.send')}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

function Bubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user';
  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      {isUser ? (
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent text-foreground">
          <User size={14} />
        </div>
      ) : (
        <FirmioAiAvatar size={28} />
      )}
      <div
        className={`min-w-0 rounded-lg px-3 py-2 ${
          isUser
            ? 'max-w-[80%] bg-primary text-primary-foreground'
            : 'flex-1 border border-border bg-background text-card-foreground'
        }`}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap text-sm">{message.content}</p>
        ) : (
          <Markdown content={message.content} />
        )}
      </div>
    </div>
  );
}

// --- Direct note ---------------------------------------------------------------

function DirectNote({ projectId }: { projectId: string }) {
  const { t } = useI18n();
  const add = useAddProjectContextEntry(projectId);
  const [content, setContent] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  const submit = async () => {
    const text = content.trim();
    if (!text) return;
    setNotice(null);
    try {
      const result = await add.mutateAsync(text);
      setContent('');
      setNotice(
        result.consolidation === 'ai'
          ? t('projects.context.manual.added')
          : t('projects.context.manual.addedAppended'),
      );
    } catch (err) {
      setNotice(err instanceof Error ? err.message : t('projects.context.error.save'));
    }
  };

  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <h3 className="text-sm font-semibold text-gray-900">{t('projects.context.manual.title')}</h3>
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={4}
        placeholder={t('projects.context.manual.placeholder')}
        className="mt-2 w-full resize-y rounded-md border border-border bg-background p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
      />
      <div className="mt-2 flex items-center justify-between gap-2">
        <p className="text-xs text-gray-600">{notice}</p>
        <button
          type="button"
          onClick={submit}
          disabled={!content.trim() || add.isPending}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
        >
          {add.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          {t('projects.context.manual.add')}
        </button>
      </div>
    </section>
  );
}

// --- Journal ------------------------------------------------------------------

function JournalPanel({
  entries,
  isLoading,
}: {
  entries: ProjectContextEntry[];
  isLoading: boolean;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <section className="rounded-lg border border-border bg-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
      >
        <div>
          <h3 className="text-sm font-semibold text-gray-900">
            {t('projects.context.journal.title')}
            {entries.length > 0 && (
              <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-normal text-gray-600">
                {entries.length}
              </span>
            )}
          </h3>
          <p className="text-xs text-gray-500">{t('projects.context.journal.hint')}</p>
        </div>
        <span className="text-xs text-gray-500">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="border-t border-border px-4 py-3">
          {isLoading ? (
            <SkeletonText lines={3} />
          ) : entries.length === 0 ? (
            <p className="text-sm text-gray-500">{t('projects.context.journal.empty')}</p>
          ) : (
            <ol className="space-y-3">
              {entries.map((e) => (
                <li key={e.id} className="rounded-md border border-border bg-background p-3">
                  <div className="mb-1 flex flex-wrap items-center gap-x-2 text-xs text-gray-500">
                    <span>{formatDateTime(e.createdAt)}</span>
                    {e.author && <span>· {e.author.name}</span>}
                    <span>· {t(`projects.context.journal.source.${e.source}`)}</span>
                  </div>
                  <p className="whitespace-pre-wrap text-sm text-gray-800">{e.content}</p>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </section>
  );
}
